import React, { Children, ForwardedRef, forwardRef, HTMLAttributes, PropsWithChildren, ReactElement } from 'react'
import { SegmentTabsProvider } from './SegmentTabs.provider'
import styled from 'styled-components'
import { SegmentTabItem } from './SegmentTabItem'

interface SegmentTabsProps<T = string> extends PropsWithChildren, HTMLAttributes<HTMLDivElement> {
  width?: string | number
  value: T
  onTabChange: (value: T) => void
}

function _SegmentTabs<T extends string>(props: SegmentTabsProps<T>, ref: ForwardedRef<HTMLDivElement>) {
  const { children, width, value, onTabChange, ...htmlDivProps } = props
  const hasDisabled = Children.toArray(children).some((child) => (child as ReactElement).props.disabled)

  const childrenArray = Children.toArray(children) as ReactElement[]

  return (
    <SegmentTabsProvider<T> value={{ selectValue: value, onTabChange }}>
      <SegmentTabsWrap style={{ width }} ref={ref} role="tablist" disabled={hasDisabled} {...htmlDivProps}>
        {childrenArray.map((child, index) =>
          React.cloneElement(child, {
            ...child.props,
            index,
            isFirst: index === 0,
            isLast: index === childrenArray.length - 1,
          }),
        )}
      </SegmentTabsWrap>
    </SegmentTabsProvider>
  )
}

const SegmentTabsWrap = styled.div<{ disabled: boolean }>`
  position: relative;
  display: flex;
  height: 36px;
  background-color: transparent;
`

const SegmentTabs = forwardRef(_SegmentTabs) as <T = string>(
  props: SegmentTabsProps<T> & { ref?: ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof _SegmentTabs>

const SegmentTabsCompound = Object.assign(SegmentTabs, {
  Item: SegmentTabItem,
})

export { SegmentTabsCompound as SegmentTabs }
